import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Award, Users, BookOpen, Quote, Newspaper, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CARDS, HERO_IMAGES, SITE } from '@/data/content';
import { NEWS } from '@/data/news';
import SEO from '@/components/SEO';

export default function Home() {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      <SEO
        title="Drug & Alcohol Awareness Program"
        description="Kress & Associates delivers the Drug and Alcohol Awareness program to grades 6-12 — real stories from law enforcement and former addicts that empower students to make the right choices."
        path="/"
      />
      {/* Hero */}
      <section className="relative h-[78vh] min-h-[560px] overflow-hidden">
        {HERO_IMAGES.map((img, i) => (
          <div
            key={img}
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-[1500ms] ${
              slide === i ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ backgroundImage: `url(${img})` }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b2545]/95 via-[#0b2545]/80 to-transparent" />

        <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/15 border border-amber-300/30 text-amber-300 text-xs font-semibold uppercase tracking-widest mb-6">
              <Shield className="h-3.5 w-3.5" /> Kress &amp; Associates
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight">
              Drug &amp; Alcohol<br />
              <span className="text-amber-400">Awareness</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-200 leading-relaxed max-w-xl">
              The Value of Making the Right Choices — a school-based prevention program
              educating grades 6–12 with real-life, unfiltered stories from law enforcement and the incarcerated.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-400 text-[#0b2545] font-semibold rounded-md">
                <Link to="/program">
                  Explore the Program <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0b2545] rounded-md">
                <Link to="/contact">Request a Presentation</Link>
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-8 text-white">
              <Stat value="25+" label="Years Law Enforcement" />
              <Stat value="85" label="Times Qualified Expert" />
              <Stat value="100%" label="Program Effectiveness" />
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              className={`h-1.5 rounded-full transition-all ${
                slide === i ? 'w-10 bg-amber-400' : 'w-4 bg-white/40 hover:bg-white/70'
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Intro strip */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-14 grid md:grid-cols-3 gap-8">
          <FeatureBadge icon={<BookOpen className="h-6 w-6" />} title="Proven Curriculum" body="Independently evaluated by the Mercyhurst College Civic Institute across 78 questions — pre and post-program." />
          <FeatureBadge icon={<Users className="h-6 w-6" />} title="Grades 6 – 12" body="Presented to middle and high school students throughout Western Pennsylvania and beyond." />
          <FeatureBadge icon={<Award className="h-6 w-6" />} title="Decorated Presenter" body="Led by Lt. Joseph M. Kress — recipient of the Medal of Valor, Purple Heart, and Proclamation for Heroism." />
        </div>
      </section>

      {/* Six category cards */}
      <section className="py-20 max-w-7xl mx-auto px-4">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-3">The Program</div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0b2545] leading-tight">
            Everything you need to know about our drug &amp; alcohol awareness initiative.
          </h2>
          <div className="mt-5 h-1 w-20 bg-amber-400" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CARDS.map((c) => (
            <Link
              key={c.id}
              to={c.to}
              className="group relative overflow-hidden rounded-lg bg-white shadow-sm hover:shadow-xl border border-slate-200 transition-all duration-300"
            >
              <div className="h-56 overflow-hidden">
                <img
                  src={c.image}
                  alt={c.title}
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-[0.25em] text-amber-600 font-bold mb-2">Learn More</div>
                <h3 className="font-serif text-2xl font-bold text-[#0b2545] mb-2">{c.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.blurb}</p>
                <div className="mt-4 inline-flex items-center text-sm font-semibold text-[#0b2545] group-hover:text-amber-600 transition-colors">
                  Read more <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Latest News */}
      <section className="bg-white py-20 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-2">Recent Activities</div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0b2545] leading-tight">
                In the news &amp; on the road.
              </h2>
              <div className="mt-5 h-1 w-20 bg-amber-400" />
            </div>
            <Button asChild variant="outline" className="border-[#0b2545] text-[#0b2545] hover:bg-[#0b2545] hover:text-white">
              <Link to="/recent-activities">
                View all <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {NEWS.slice(0, 2).map((n) => (
              <Link
                key={n.id}
                to={`/recent-activities/${n.id}`}
                className="group grid grid-cols-[140px_1fr] md:grid-cols-[200px_1fr] gap-5 bg-slate-50 rounded-lg overflow-hidden border border-slate-200 hover:shadow-md hover:border-amber-300 transition-all"
              >
                <div className="overflow-hidden">
                  <img
                    src={n.cover}
                    alt={n.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 pr-6 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                    <span className="flex items-center gap-1"><Newspaper className="h-3.5 w-3.5 text-amber-500" /> {n.source}</span>
                    <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-amber-500" /> {n.date}</span>
                  </div>
                  <h3 className="font-serif text-lg md:text-xl font-bold text-[#0b2545] leading-snug mb-2 group-hover:text-amber-600 transition-colors">
                    {n.title}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-3">{n.summary}</p>
                  <div className="mt-3 inline-flex items-center text-xs font-semibold text-[#0b2545] group-hover:text-amber-600 transition-colors">
                    Read story <ArrowRight className="ml-1.5 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* Testimonial banner */}
      <section className="bg-[#0b2545] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Quote className="h-12 w-12 text-amber-400 mx-auto mb-6" />
          <p className="font-serif text-2xl md:text-3xl leading-relaxed italic">
            “Your presentation is one of the strongest and most lasting speeches our students will see
            in their high school career. The impact you and your team provided is immeasurable.”
          </p>
          <div className="mt-8 text-amber-300 font-semibold tracking-wide">Daniel L. Daum</div>
          <div className="text-slate-300 text-sm">Principal, Corry Area High School</div>
          <Button asChild variant="outline" className="mt-8 bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0b2545]">
            <Link to="/endorsements">Read All Endorsements</Link>
          </Button>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-3">Get Involved</div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0b2545]">
            Bring the program to your school.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Contact Kress &amp; Associates to schedule a presentation for your students, staff, or community.
            Every school district that hosts the program is provided a “Make the Right Choice Today” sign.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="bg-[#0b2545] hover:bg-[#123a6b] text-white rounded-md">
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-[#0b2545] text-[#0b2545] hover:bg-[#0b2545] hover:text-white rounded-md">
              <a href={`tel:${SITE.phone.replace(/[^0-9]/g, '')}`}>Call {SITE.phone}</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-serif text-3xl md:text-4xl font-bold text-amber-400">{value}</div>
      <div className="text-xs uppercase tracking-widest text-slate-300 mt-1">{label}</div>
    </div>
  );
}

function FeatureBadge({ icon, title, body }) {
  return (
    <div className="flex gap-4">
      <div className="h-12 w-12 rounded-md bg-[#0b2545] text-amber-400 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#0b2545] mb-1">{title}</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
